import Decimal from 'decimal.js';
import { calculateVolume, calculateWeight } from './volumeUtils';
import { getAdjustedCargoTemperature } from './adjustedCargoTemperatureUtils';
import { getCargoTemperatureFromCommodityBook } from './cargoTemperatureUtils';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { calculateFillRatio, stowedWeightForCargoOperationInOtherTanks } from './stowageOperationUtils';
import { getMaxVolumeForCargoType } from './stowageFillUtils';
import { roundTo3Decimals } from 'src/utils/numberHelper';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';

const FILL_RATIO_MAX: Decimal = new Decimal(1);
export const FILL_RATIO_98_PCT: Decimal = new Decimal(0.98);

// TODO: REFACTORING - Move this type to a shared types file when refactoring is complete
// For now, we'll define a minimal interface for what the utility functions need
export interface StowageVMForUtils {
    initialContentWeight: Decimal;
    stowedWeight: Decimal;
    isLoading?: boolean;
    overrideFillRatio?: boolean;
    cargoTemperatureInTank?: number | null;
    tank: Tank;
}

/**
 * Calculate the final content weight after a stowage operation
 *
 * Bug 31115 Fix: For unloading operations, stowedWeight is displayed as positive in UI
 * but should be subtracted from initialContentWeight to get final content weight
 *
 * @param stowageVM - The stowage view model containing operation details
 * @returns The calculated content weight as a Decimal
 */
export const calculateContentWeight = (stowageVM: StowageVMForUtils): Decimal => {
    return stowageVM.isLoading
        ? stowageVM.initialContentWeight.add(stowageVM.stowedWeight) // Loading: add the stowed weight
        : stowageVM.initialContentWeight.minus(stowageVM.stowedWeight); // Unloading: subtract the unstowed weight
};

/**
 * Calculate the final content volume after a stowage operation
 *
 * Bug 31115 Fix: For unloading operations, stowedWeight is displayed as positive in UI
 * but should be subtracted from initialContentWeight to get final content volume
 *
 * @param stowageVM - The stowage view model containing operation details
 * @param cargoOperation - The cargo operation details
 * @param stowagePlan - The active stowage plan
 * @returns The calculated content volume as a Decimal
 */
export const calculateContentVolume = (stowageVM: StowageVMForUtils, cargoOperation: RotationCargoOperation, stowagePlan: StowagePlan): Decimal => {
    // Bug 31115 Fix: For unloading operations, stowedWeight is displayed as positive in UI
    // but should be subtracted from initialContentWeight to get final content volume
    const finalContentWeight = stowageVM.isLoading
        ? stowageVM.initialContentWeight.add(stowageVM.stowedWeight) // Loading: add the stowed weight
        : stowageVM.initialContentWeight.minus(stowageVM.stowedWeight); // Unloading: subtract the unstowed weight

    return calculateVolume(finalContentWeight, cargoOperation.cargo, getActualCargoTemperature(stowageVM, cargoOperation, stowagePlan));
};

/**
 * Get the maximum fill ratio based on override settings
 *
 * @param stowageVM - The stowage view model containing override settings
 * @returns The maximum fill ratio as a Decimal (1.0 for 100% or 0.98 for 98%)
 */
export const getMaxFillRatio = (stowageVM: StowageVMForUtils): Decimal => {
    return stowageVM.overrideFillRatio ? FILL_RATIO_MAX : FILL_RATIO_98_PCT;
};

/**
 * Get the cargo temperature in tank from the view model
 *
 * @param vm - The stowage view model
 * @returns The cargo temperature in tank as a number
 */
export const getCargoTemperatureInTank = (vm: StowageVMForUtils): number => {
    return vm.cargoTemperatureInTank;
};

/**
 * Get the actual cargo temperature, considering tank temperature, adjusted temperature, or commodity book temperature
 *
 * @param vm - The stowage view model
 * @param cargoOperation - The cargo operation details
 * @param stowagePlan - The active stowage plan
 * @returns The actual cargo temperature as a number
 */
export const getActualCargoTemperature = (vm: StowageVMForUtils, cargoOperation: RotationCargoOperation, stowagePlan: StowagePlan): number => {
    return (
        getCargoTemperatureInTank(vm) ??
        getAdjustedCargoTemperature(stowagePlan, cargoOperation.cargo)?.temperature ??
        getCargoTemperatureFromCommodityBook(cargoOperation.cargo)
    );
};

/**
 * Get the tank description based on fill ratio override
 *
 * @param stowageVM - The stowage view model containing override settings
 * @returns A string describing the tank volume calculation basis
 */
export const getTankDescription = (stowageVM: StowageVMForUtils): string => {
    return stowageVM.overrideFillRatio ? 'Volume at 100%' : 'Volume at 98%';
};

/**
 * Get the tank volume based on fill ratio override
 *
 * @param stowageVM - The stowage view model containing tank and override settings
 * @returns The tank volume as a number (100% or 98% volume)
 */
export const getTankVolume = (stowageVM: StowageVMForUtils): number => {
    return stowageVM.overrideFillRatio ? stowageVM.tank.volume : stowageVM.tank.volumeAt98Percent;
};

/**
 * Validation Functions
 */

/**
 * Validate if stowed weight is within acceptable range
 *
 * @param stowedWeight - The stowed weight to validate
 * @param maxStowageWeight - Maximum allowed stowage weight
 * @returns True if valid, false otherwise
 */
export const isStowedWeightValid = (stowedWeight: Decimal, maxStowageWeight: Decimal): boolean => {
    return stowedWeight.toDecimalPlaces(3).gte(0) && stowedWeight.toDecimalPlaces(3).lte(maxStowageWeight);
};

/**
 * Validate if stowed volume is within acceptable range
 *
 * @param stowedVolume - The stowed volume to validate
 * @param maxStowageVolume - Maximum allowed stowage volume
 * @returns True if valid, false otherwise
 */
export const isStowedVolumeValid = (stowedVolume: Decimal, maxStowageVolume: Decimal): boolean => {
    return new Decimal(0).lte(stowedVolume.toDecimalPlaces(3)) && stowedVolume.toDecimalPlaces(3).lte(maxStowageVolume);
};

/**
 * Validate if fill ratio is within acceptable range
 *
 * @param fillRatio - The fill ratio to validate
 * @param maxFillRatio - Maximum allowed fill ratio
 * @returns True if valid, false otherwise
 */
export const isFillRatioValid = (fillRatio: Decimal, maxFillRatio: Decimal): boolean => {
    return new Decimal(0).lte(fillRatio.toDecimalPlaces(3)) && fillRatio.toDecimalPlaces(3).lte(maxFillRatio);
};

/**
 * Calculate not stowed weight for a cargo operation
 *
 * @param stowageVM - The stowage view model
 * @param cargoOperation - The cargo operation
 * @param activeStowagePlan - The active stowage plan
 * @returns The not stowed weight as a Decimal
 */
export const calculateNotStowedWeightForCargoOperation = (
    stowageVM: StowageVMForUtils,
    cargoOperation: RotationCargoOperation,
    activeStowagePlan: StowagePlan
): Decimal => {
    const weightToBeStowed = cargoOperation.nominatedWeight ?? 0;
    const stowedWeightInOtherTanks = stowedWeightForCargoOperationInOtherTanks(activeStowagePlan, stowageVM.tank, cargoOperation)
        .times(stowageVM.isLoading ? 1 : -1)
        .toDecimalPlaces(3); // Round to 3 decimal places to match precision
    const stowedWeightRegisteredInForm = stowageVM.stowedWeight;
    const notStowedWeight = new Decimal(weightToBeStowed).minus(stowedWeightInOtherTanks).minus(stowedWeightRegisteredInForm);

    return notStowedWeight;
};

/**
 * Calculate not stowed volume for a cargo operation
 *
 * @param stowageVM - The stowage view model
 * @param cargoOperation - The cargo operation
 * @param activeStowagePlan - The active stowage plan
 * @returns The not stowed volume as a Decimal
 */
export const calculateNotStowedVolumeForCargoOperation = (
    stowageVM: StowageVMForUtils,
    cargoOperation: RotationCargoOperation,
    activeStowagePlan: StowagePlan
): Decimal => {
    const notStowedWeight = calculateNotStowedWeightForCargoOperation(stowageVM, cargoOperation, activeStowagePlan);
    const actualCargoTemperature = getActualCargoTemperature(stowageVM, cargoOperation, activeStowagePlan);
    return calculateVolume(notStowedWeight, cargoOperation.cargo, actualCargoTemperature);
};

/**
 * Get maximum stowage volume based on cargo type and tank settings
 *
 * @param stowageVM - The stowage view model
 * @param cargoOperation - The cargo operation
 * @returns The maximum stowage volume as a Decimal
 */
export const getMaxStowageVolume = (stowageVM: StowageVMForUtils, cargoOperation: RotationCargoOperation): Decimal => {
    const maxVolumeForCargoType = getMaxVolumeForCargoType(cargoOperation.cargo);
    const maxVolumeForTank = new Decimal(stowageVM.overrideFillRatio ? stowageVM.tank.volume : stowageVM.tank.volumeAt98Percent);
    return Decimal.min(maxVolumeForTank, maxVolumeForCargoType);
};

/**
 * Get maximum stowage weight based on nominated weight and tank limits
 *
 * @param stowageVM - The stowage view model
 * @param cargoNominatedWeight - The cargo nominated weight
 * @returns The maximum stowage weight as a Decimal
 */
export const getMaxStowageWeight = (stowageVM: StowageVMForUtils, cargoNominatedWeight: number | null): Decimal => {
    const nominatedWeight = cargoNominatedWeight ?? 0;
    const maxWeightAt98Percent = stowageVM.tank.maxWeightAt98Percent ?? 0;
    return new Decimal(nominatedWeight < maxWeightAt98Percent ? nominatedWeight : maxWeightAt98Percent);
};

/**
 * Generate total nominated swing weight display string
 *
 * @param cargoNominatedWeight - The cargo nominated weight
 * @param swingValues - The swing values containing min/max weights
 * @returns Formatted string showing nominated weight and swing range
 */
export const getTotalNominatedSwingWeight = (cargoNominatedWeight: number | null, swingValues: any): string => {
    const nominatedWeight = cargoNominatedWeight ? roundTo3Decimals(cargoNominatedWeight).toFixed(3) : 'NA';
    const nominatedWeightMin = swingValues.minWeight ? roundTo3Decimals(swingValues.minWeight).toFixed(3) : 'NA';
    const nominatedWeightMax = swingValues.maxWeight ? roundTo3Decimals(swingValues.maxWeight).toFixed(3) : 'NA';

    return `${nominatedWeight} / [${nominatedWeightMin}, ${nominatedWeightMax}]`;
};

/**
 * Generate total nominated swing volume display string
 *
 * @param cargoNominatedWeight - The cargo nominated weight
 * @param swingValues - The swing values containing min/max weights
 * @param cargoOperation - The cargo operation
 * @param actualCargoTemperature - The actual cargo temperature
 * @returns Formatted string showing nominated volume and swing range
 */
export const getTotalNominatedSwingVolume = (
    cargoNominatedWeight: number | null,
    swingValues: any,
    cargoOperation: RotationCargoOperation,
    actualCargoTemperature: number
): string => {
    const nominatedVolume = cargoNominatedWeight
        ? roundTo3Decimals(calculateVolume(new Decimal(cargoNominatedWeight), cargoOperation.cargo, actualCargoTemperature).toNumber()).toFixed(3)
        : 'NA';
    const nominatedVolumeMin = swingValues.minWeight
        ? roundTo3Decimals(calculateVolume(new Decimal(swingValues.minWeight), cargoOperation.cargo, actualCargoTemperature).toNumber()).toFixed(3)
        : 'NA';
    const nominatedVolumeMax = swingValues.maxWeight
        ? roundTo3Decimals(calculateVolume(new Decimal(swingValues.maxWeight), cargoOperation.cargo, actualCargoTemperature).toNumber()).toFixed(3)
        : 'NA';

    return `${nominatedVolume} / [${nominatedVolumeMin}, ${nominatedVolumeMax}]`;
};

/**
 * Generate weight remaining error message
 *
 * @param remainingWeight - The remaining weight
 * @param stowedWeight - The current stowed weight
 * @returns Error message string
 */
export const getWeightRemainingErrorMessage = (remainingWeight: Decimal, stowedWeight: Decimal): string => {
    const maxAllowedWeight = stowedWeight.add(remainingWeight);
    return `The value must not exceed ${roundTo3Decimals(maxAllowedWeight.toNumber()).toFixed(3)} MT`;
};

/**
 * Generate volume remaining error message
 *
 * @param remainingVolume - The remaining volume
 * @param stowedVolume - The current stowed volume
 * @returns Error message string
 */
export const getVolumeRemainingErrorMessage = (remainingVolume: Decimal, stowedVolume: Decimal): string => {
    const maxAllowedVolume = stowedVolume.add(remainingVolume);
    return `The value must not exceed ${roundTo3Decimals(maxAllowedVolume.toNumber()).toFixed(3)} m³`;
};

/**
 * Update stowed volume and fill ratio when stowed weight changes
 *
 * @param vm - The stowage view model to update
 * @param cargoOperation - The cargo operation
 * @param actualCargoTemperature - The actual cargo temperature
 */
export const updateStowedWeight = (
    vm: StowageVMForUtils & { stowedVolume: Decimal; fillRatio: Decimal },
    cargoOperation: RotationCargoOperation,
    actualCargoTemperature: number
) => {
    vm.stowedVolume = calculateVolume(vm.stowedWeight, cargoOperation.cargo, actualCargoTemperature);
    vm.fillRatio = calculateFillRatio(
        vm.isLoading
            ? calculateVolume(vm.initialContentWeight.add(vm.stowedWeight), cargoOperation.cargo, actualCargoTemperature)
            : calculateVolume(vm.initialContentWeight.minus(vm.stowedWeight), cargoOperation.cargo, actualCargoTemperature),
        vm.tank
    );
};

/**
 * Update stowed weight and fill ratio when stowed volume changes
 *
 * @param vm - The stowage view model to update
 * @param cargoOperation - The cargo operation
 * @param actualCargoTemperature - The actual cargo temperature
 */
export const updateStowedVolume = (
    vm: StowageVMForUtils & { stowedWeight: Decimal; stowedVolume: Decimal; fillRatio: Decimal },
    cargoOperation: RotationCargoOperation,
    actualCargoTemperature: number
) => {
    vm.stowedWeight = calculateWeight(vm.stowedVolume, cargoOperation.cargo, actualCargoTemperature);
    vm.fillRatio = calculateFillRatio(
        vm.isLoading
            ? calculateVolume(vm.initialContentWeight, cargoOperation.cargo, actualCargoTemperature).plus(vm.stowedVolume)
            : calculateVolume(vm.initialContentWeight, cargoOperation.cargo, actualCargoTemperature).minus(vm.stowedVolume),
        vm.tank
    );
};

/**
 * Update stowed weight and volume when fill ratio changes
 *
 * @param vm - The stowage view model to update
 * @param cargoOperation - The cargo operation
 * @param actualCargoTemperature - The actual cargo temperature
 */
export const updateFillRatio = (
    vm: StowageVMForUtils & { stowedWeight: Decimal; stowedVolume: Decimal; fillRatio: Decimal },
    cargoOperation: RotationCargoOperation,
    actualCargoTemperature: number
) => {
    vm.stowedVolume = vm.isLoading
        ? vm.fillRatio.times(vm.tank.volume).minus(calculateVolume(vm.initialContentWeight, cargoOperation.cargo, actualCargoTemperature))
        : calculateVolume(vm.initialContentWeight, cargoOperation.cargo, actualCargoTemperature).minus(vm.fillRatio.times(vm.tank.volume));

    vm.stowedWeight = calculateWeight(vm.stowedVolume, cargoOperation.cargo, actualCargoTemperature);
};

/**
 * Update stowed volume and fill ratio when cargo temperature changes
 *
 * @param vm - The stowage view model to update
 * @param cargoOperation - The cargo operation
 */
export const updateCargoTemperature = (
    vm: StowageVMForUtils & { stowedVolume: Decimal; fillRatio: Decimal },
    cargoOperation: RotationCargoOperation
) => {
    vm.stowedVolume = calculateVolume(vm.stowedWeight, cargoOperation.cargo, vm.cargoTemperatureInTank);
    vm.fillRatio = calculateFillRatio(
        vm.isLoading
            ? calculateVolume(vm.initialContentWeight, cargoOperation.cargo, vm.cargoTemperatureInTank).plus(vm.stowedVolume)
            : calculateVolume(vm.initialContentWeight, cargoOperation.cargo, vm.cargoTemperatureInTank).minus(vm.stowedVolume),
        vm.tank
    );
};

/**
 * Check if save operation is valid
 *
 * @param vm - The stowage view model
 * @param notStowedWeight - The not stowed weight
 * @param notStowedVolume - The not stowed volume
 * @returns True if save is valid, false otherwise
 */
export const canSaveStowage = (
    vm: StowageVMForUtils & { hasInvalidFields?: boolean; stowedWeight: Decimal },
    notStowedWeight: Decimal,
    notStowedVolume: Decimal
): boolean => {
    const hasValidWeightRemaining = notStowedWeight.gte(0);
    const hasValidVolumeRemaining = notStowedVolume.gte(0);
    return !vm.hasInvalidFields && hasValidWeightRemaining && hasValidVolumeRemaining && ((vm.isLoading && vm.stowedWeight.gte(0)) || !vm.isLoading);
};
